import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { createSearchParams, Navigate, useLocation, useNavigate, useNavigationType, type NavigateOptions, type URLSearchParamsInit } from "react-router-dom";
import { createListMemory, decodeListValue, encodeListValue, rememberedSearch, shouldRestoreListSearch, type ListValue } from "./listStateStorage";

type ListContext = {
  search: string;
  update: (change: (params: URLSearchParams) => URLSearchParams | void, options?: NavigateOptions) => void;
};
const ListStateContext = createContext<ListContext | null>(null);

/** URL state handles browser Back; scoped session memory handles plain list/back links. */
export function ListStateProvider({ scope, routeKeys, children }: {
  scope: string;
  routeKeys: (pathname: string) => readonly string[];
  children: ReactNode;
}) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const [memory] = useState(() => createListMemory(scope));
  const previous = useRef<{ pathname: string; key: string } | null>(null);
  const saved = memory.get(location.pathname);
  // An explicit URL (including browser history) always wins. A plain in-app
  // return link restores the last list, without mounting/fetching its defaults.
  const restore = shouldRestoreListSearch(previous.current, location, navigationType, saved);
  const search = restore ? saved : location.search;
  const pending = useRef({ pathname: location.pathname, committed: search, search });
  if (pending.current.pathname !== location.pathname || pending.current.committed !== search) {
    pending.current = { pathname: location.pathname, committed: search, search };
  }

  useLayoutEffect(() => {
    if (restore) return;
    previous.current = { pathname: location.pathname, key: location.key };
    memory.set(location.pathname, rememberedSearch(location.search, routeKeys(location.pathname)));
  }, [location.key, location.pathname, location.search, memory, restore, routeKeys]);

  const update = useCallback((change: (params: URLSearchParams) => URLSearchParams | void, options?: NavigateOptions) => {
    // Ignore an outgoing page effect while a different route is rendering.
    if (pending.current.pathname !== location.pathname) return;
    const params = new URLSearchParams(pending.current.search);
    const next = (change(params) ?? params).toString();
    const nextSearch = next ? `?${next}` : "";
    if (nextSearch === pending.current.search) return;
    pending.current.search = nextSearch;
    // Merge synchronous changes (e.g. filter + page reset), unlike independent
    // setSearchParams calls which can overwrite each other in the same event.
    navigate({ pathname: location.pathname, search: pending.current.search, hash: location.hash }, { replace: true, state: location.state, ...options });
  }, [navigate, location.pathname, location.hash, location.state]);
  const context = useMemo(() => ({ search, update }), [search, update]);
  if (restore) return <Navigate to={{ pathname: location.pathname, search, hash: location.hash }} state={location.state} replace />;
  return <ListStateContext.Provider key={location.pathname} value={context}>{children}</ListStateContext.Provider>;
}

/** Share the pending URL with list filters so tab changes and page resets cannot overwrite each other. */
export function useListSearchParams(): [URLSearchParams, (
  next: URLSearchParamsInit | ((current: URLSearchParams) => URLSearchParamsInit), options?: NavigateOptions
) => void] {
  const context = useContext(ListStateContext);
  if (!context) throw new Error("List navigation must be inside ListStateProvider.");
  const params = useMemo(() => new URLSearchParams(context.search), [context.search]);
  const { update } = context;
  const setParams = useCallback((next: URLSearchParamsInit | ((current: URLSearchParams) => URLSearchParamsInit), options?: NavigateOptions) => {
    update(current => createSearchParams(typeof next === "function" ? next(current) : next), { replace: false, ...options });
  }, [update]);
  return [params, setParams];
}

/** Persist only list controls, never form drafts, selected records or actions. */
export function useListState(name: string, initial: string | (() => string), queryKey?: string): [string, Dispatch<SetStateAction<string>>];
export function useListState(name: string, initial: number | (() => number), queryKey?: string): [number, Dispatch<SetStateAction<number>>];
export function useListState(name: string, initial: boolean | (() => boolean), queryKey?: string): [boolean, Dispatch<SetStateAction<boolean>>];
export function useListState<T extends ListValue>(name: string, initial: T | (() => T), queryKey?: string): [T, Dispatch<SetStateAction<T>>];
export function useListState<T extends ListValue>(name: string, initial: T | (() => T), queryKey = `f.${name}`): [T, Dispatch<SetStateAction<T>>] {
  const context = useContext(ListStateContext);
  if (!context) throw new Error("List controls must be inside ListStateProvider.");
  const [fallback] = useState(initial);
  const raw = new URLSearchParams(context.search).get(queryKey);
  const value = useMemo(() => decodeListValue(raw, fallback), [raw, fallback]);
  const { update } = context;
  const setValue = useCallback<Dispatch<SetStateAction<T>>>((next) => update(params => {
    const current = decodeListValue(params.get(queryKey), fallback);
    const resolved = typeof next === "function" ? next(current) : next;
    // Keep explicit defaults so clearing a filter cannot resurrect saved state.
    params.set(queryKey, encodeListValue(resolved));
  }), [fallback, queryKey, update]);
  return [value, setValue];
}

/** Reset pagination when filters change, not when a filtered list remounts. */
export function useListFilterChange(key: string, reset: () => void) {
  const previous = useRef(key);
  const navigationType = useNavigationType();
  useLayoutEffect(() => {
    if (previous.current === key) return;
    previous.current = key;
    if (navigationType !== "POP") reset();
  }, [key, reset, navigationType]);
}
